/**
 * 共享追踪上下文 - 内存状态存储
 * 供 error-tracking SDK 和 analytics SDK 共同使用
 */
import { generateUUID } from '@/lib/utils';

/** tab 粒度 sessionId，每次页面加载时生成一次，不持久化 */
export const SESSION_ID = generateUUID();

/** 路由名称映射表（pathname → 稳定路由名） */
const ROUTE_MAP: [RegExp, string][] = [
  [/^\/project\/parse-flow/, 'parse-flow'],
  [/^\/project\/[^/]+\/parse-flow/, 'parse-flow'],
  [/^\/project\/[^/]+/, 'project'],
  [/^\/notifications/, 'notifications'],
  [/^\/account\/profile/, 'account-profile'],
  [/^\/account\/transactions/, 'account-transactions'],
  [/^\/account\/consumption/, 'account-consumption'],
  [/^\/account\//, 'account'],
  // /shared 精确匹配 /shared 本身或 /shared/:orgId
  [/^\/shared($|\/)/, 'my-shares'],
  [/^\/my-orgs/, 'my-orgs'],
  [/^\/org\/[^/]+/, 'org-settings'],
  [/^\/workspace\/[^/]+/, 'workspace-folder'],
  [/^\/workspace/, 'workspace'],
  [/^\/collab\/[^/]+/, 'collab-folder'],
  [/^\/collab/, 'collab'],
  [/^\/trash/, 'trash'],
  [/^\/settings/, 'settings'],
  [/^\/share\/p\//, 'share-project'],
  [/^\/register/, 'register'],
  [/^\/forgot-password/, 'forgot-password'],
  [/^\/login/, 'login'],
  [/^\/demo\/realtime-asr/, 'demo-realtime-asr'],
  [/^\/$/, 'home'],
];

/**
 * 将 pathname 归一化为稳定路由名
 * 路由表随项目路由结构维护，新增路由时同步更新此映射
 */
export function resolveRouteName(pathname: string): string {
  for (const [pattern, name] of ROUTE_MAP) {
    if (pattern.test(pathname)) return name;
  }
  return 'unknown';
}

// ─── 内存态上下文 ───────────────────────────────────────────────

export interface UserContext {
  userId: string;
  teamId?: string;
  projectId?: string;
}

export interface RouteContext {
  pathname: string;
  routeName: string;
}

export interface ProjectContext {
  projectId: string;
  teamId?: string;
}

let userContext: UserContext | null = null;
let routeContext: RouteContext = {
  pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
  routeName: resolveRouteName(typeof window !== 'undefined' ? window.location.pathname : '/'),
};
let projectContext: ProjectContext | null = null;

export function getSessionId(): string {
  return SESSION_ID;
}

export function setUserContext(ctx: UserContext | null): void {
  userContext = ctx;
}

export function getUserContext(): UserContext | null {
  return userContext;
}

export function setRouteContext(ctx: RouteContext): void {
  routeContext = ctx;
}

export function getRouteContext(): RouteContext {
  return routeContext;
}

export function setProjectContext(ctx: ProjectContext | null): void {
  projectContext = ctx;
}

export function getProjectContext(): ProjectContext | null {
  return projectContext;
}
